import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { FieldMapping } from '../../services/fieldMapping.service';
import { isFieldHidden, isFieldRequired } from '../../utils/formUtils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface PreviousEmploymentHistoryProps {
  data?: any;
  onChange?: (data: any) => void;
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
}

interface EmploymentInstance {
  date_started: string;
  date_stopped: string;
  employer_name: string;
  employer_city_state: string;
  job_title: string;
  job_description: string;
  time_to_learn_job: string;
  union_name_local: string;
  number_people_supervised: string;
  shift_1st: boolean;
  shift_2nd: boolean;
  shift_3rd: boolean;
  shift_rotating: boolean;
  machinery_operated: string;
  hours_sitting: string;
  hours_standing: string;
  hours_walking: string;
  weight_lifted_pounds: string;
  items_lifted: string;
  salary_amount: string;
  salary_frequency: string;
  hours_per_day: string;
  overtime_opportunities: string;
  overtime_hours_per_week: string;
  reason_for_leaving: string;
}

interface FormData {
  previous_employment_1: EmploymentInstance;
  previous_employment_2: EmploymentInstance;
  previous_employment_3: EmploymentInstance;
}

const PreviousEmploymentHistory: React.FC<PreviousEmploymentHistoryProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [],
  readOnly = false, // eslint-disable-line @typescript-eslint/no-unused-vars
}) => {
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => { /* noop */ });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isFieldHiddenLocal  = (f: string) => isFieldHidden(f, 'previous_employment_history', fieldMappings);
  const isFieldRequiredLocal = (f: string) => isFieldRequired(f, 'previous_employment_history', fieldMappings);
  const [formData, setFormData] = useState<FormData>({
    previous_employment_1: {
      date_started: '',
      date_stopped: '',
      employer_name: '',
      employer_city_state: '',
      job_title: '',
      job_description: '',
      time_to_learn_job: '',
      union_name_local: '',
      number_people_supervised: '',
      shift_1st: false,
      shift_2nd: false,
      shift_3rd: false,
      shift_rotating: false,
      machinery_operated: '',
      hours_sitting: '',
      hours_standing: '',
      hours_walking: '',
      weight_lifted_pounds: '',
      items_lifted: '',
      salary_amount: '',
      salary_frequency: '',
      hours_per_day: '',
      overtime_opportunities: '',
      overtime_hours_per_week: '',
      reason_for_leaving: '',
    },
    previous_employment_2: {
      date_started: '',
      date_stopped: '',
      employer_name: '',
      employer_city_state: '',
      job_title: '',
      job_description: '',
      time_to_learn_job: '',
      union_name_local: '',
      number_people_supervised: '',
      shift_1st: false,
      shift_2nd: false,
      shift_3rd: false,
      shift_rotating: false,
      machinery_operated: '',
      hours_sitting: '',
      hours_standing: '',
      hours_walking: '',
      weight_lifted_pounds: '',
      items_lifted: '',
      salary_amount: '',
      salary_frequency: '',
      hours_per_day: '',
      overtime_opportunities: '',
      overtime_hours_per_week: '',
      reason_for_leaving: '',
    },
    previous_employment_3: {
      date_started: '',
      date_stopped: '',
      employer_name: '',
      employer_city_state: '',
      job_title: '',
      job_description: '',
      time_to_learn_job: '',
      union_name_local: '',
      number_people_supervised: '',
      shift_1st: false,
      shift_2nd: false,
      shift_3rd: false,
      shift_rotating: false,
      machinery_operated: '',
      hours_sitting: '',
      hours_standing: '',
      hours_walking: '',
      weight_lifted_pounds: '',
      items_lifted: '',
      salary_amount: '',
      salary_frequency: '',
      hours_per_day: '',
      overtime_opportunities: '',
      overtime_hours_per_week: '',
      reason_for_leaving: '',
    },
    ...actualFormData,
  });

  useEffect(() => {
    setFormData((prev: FormData) => ({ ...prev, ...actualFormData }));
  }, [actualFormData]);

  const handleChange = (instance: string, field: string, value: any) => {
    const updatedData = {
      ...formData,
      [instance]: {
        ...formData[instance as keyof FormData],
        [field]: value,
      },
    };
    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  const renderEmploymentInstance = (
    instanceKey: string,
    instanceNumber: number
  ) => {
    const instance = formData[
      instanceKey as keyof FormData
    ] as EmploymentInstance;

    return (
      <Accordion key={instanceKey}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='h6'>
            Previous Employment History #{instanceNumber}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Employment Dates */}
            <Box>
              <Typography variant='h6' gutterBottom>
                Employment Period
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label='Date Started (Specify Year)'
                  type='date'
                  value={instance.date_started}
                  onChange={e =>
                    handleChange(instanceKey, 'date_started', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <TextField
                  label='Date Stopped (Specify Year)'
                  type='date'
                  value={instance.date_stopped}
                  onChange={e =>
                    handleChange(instanceKey, 'date_stopped', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              </Box>
            </Box>

            {/* Employer Information */}
            <Box>
              <Typography variant='h6' gutterBottom>
                Employer Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label='Employer Name'
                  value={instance.employer_name}
                  onChange={e => handleChange(instanceKey, 'employer_name', e.target.value)}
                  required={isFieldRequiredLocal('employer_name')}
                />
                <TextField
                  fullWidth
                  label='City & State'
                  value={instance.employer_city_state}
                  onChange={e =>
                    handleChange(
                      instanceKey,
                      'employer_city_state',
                      e.target.value
                    )
                  }
                />
                <TextField
                  fullWidth
                  label='Job Title'
                  value={instance.job_title}
                  onChange={e =>
                    handleChange(instanceKey, 'job_title', e.target.value)
                  }
                />
                <TextField
                  fullWidth
                  label='Job Description (Describe in Detail & Attach any necessary documents relevant)'
                  multiline
                  rows={4}
                  value={instance.job_description}
                  onChange={e =>
                    handleChange(instanceKey, 'job_description', e.target.value)
                  }
                />
              </Box>
            </Box>

            {/* Job Details */}
            <Box>
              <Typography variant='h6' gutterBottom>
                Job Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label='How Long did it take you to learn the Job?'
                  value={instance.time_to_learn_job}
                  onChange={e =>
                    handleChange(
                      instanceKey,
                      'time_to_learn_job',
                      e.target.value
                    )
                  }
                />
                <TextField
                  fullWidth
                  label='Union? (Provide Name and Local)'
                  value={instance.union_name_local}
                  onChange={e =>
                    handleChange(
                      instanceKey,
                      'union_name_local',
                      e.target.value
                    )
                  }
                />
                <TextField
                  fullWidth
                  label='Number of people Supervised?'
                  value={instance.number_people_supervised}
                  onChange={e =>
                    handleChange(
                      instanceKey,
                      'number_people_supervised',
                      e.target.value
                    )
                  }
                />
              </Box>
            </Box>

            {/* Shifts */}
            <Box>
              <Typography variant='h6' gutterBottom>
                Please specify the shift while on the job
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={instance.shift_1st}
                      onChange={e =>
                        handleChange(instanceKey, 'shift_1st', e.target.checked)
                      }
                    />
                  }
                  label='1st Shift'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={instance.shift_2nd}
                      onChange={e =>
                        handleChange(instanceKey, 'shift_2nd', e.target.checked)
                      }
                    />
                  }
                  label='2nd Shift'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={instance.shift_3rd}
                      onChange={e =>
                        handleChange(instanceKey, 'shift_3rd', e.target.checked)
                      }
                    />
                  }
                  label='3rd Shift'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={instance.shift_rotating}
                      onChange={e =>
                        handleChange(
                          instanceKey,
                          'shift_rotating',
                          e.target.checked
                        )
                      }
                    />
                  }
                  label='Rotating/Other'
                />
              </Box>
            </Box>

            {/* Machinery */}
            <Box>
              <Typography variant='h6' gutterBottom>
                Machinery
              </Typography>
              <TextField
                fullWidth
                label='Did you Operate any Machinery? (Type)'
                value={instance.machinery_operated}
                onChange={e =>
                  handleChange(
                    instanceKey,
                    'machinery_operated',
                    e.target.value
                  )
                }
              />
            </Box>

            {/* Physical Demands */}
            <Box>
              <Typography variant='h6' gutterBottom>
                Physical Demands
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label='How Many Hours Did you Sit on the Job?'
                  value={instance.hours_sitting}
                  onChange={e =>
                    handleChange(instanceKey, 'hours_sitting', e.target.value)
                  }
                />
                <TextField
                  fullWidth
                  label='How Many Hours Did you Stand on the Job?'
                  value={instance.hours_standing}
                  onChange={e =>
                    handleChange(instanceKey, 'hours_standing', e.target.value)
                  }
                />
                <TextField
                  fullWidth
                  label='How Many Hours Did you have to Walk on the Job?'
                  value={instance.hours_walking}
                  onChange={e =>
                    handleChange(instanceKey, 'hours_walking', e.target.value)
                  }
                />
                <TextField
                  fullWidth
                  label='How much weight did you lift on the job in pounds?'
                  value={instance.weight_lifted_pounds}
                  onChange={e =>
                    handleChange(
                      instanceKey,
                      'weight_lifted_pounds',
                      e.target.value
                    )
                  }
                />
                <TextField
                  fullWidth
                  label='What did you lift? (Materials, Products, Items, etc.)'
                  multiline
                  rows={3}
                  value={instance.items_lifted}
                  onChange={e =>
                    handleChange(instanceKey, 'items_lifted', e.target.value)
                  }
                />
              </Box>
            </Box>

            {/* Salary and Hours */}
            <Box>
              <Typography variant='h6' gutterBottom>
                Salary and Hours
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='subtitle1'>Gross Salary?</Typography>
                  <FormControl component='fieldset'>
                    <RadioGroup
                      value={instance.salary_frequency}
                      onChange={e =>
                        handleChange(
                          instanceKey,
                          'salary_frequency',
                          e.target.value
                        )
                      }
                      sx={{ flexDirection: 'row' }}
                    >
                      <FormControlLabel
                        value='weekly'
                        control={<Radio />}
                        label='Weekly'
                      />
                      <FormControlLabel
                        value='monthly'
                        control={<Radio />}
                        label='Monthly'
                      />
                      <FormControlLabel
                        value='yearly'
                        control={<Radio />}
                        label='Yearly'
                      />
                    </RadioGroup>
                  </FormControl>
                  <TextField
                    label='$'
                    value={instance.salary_amount}
                    onChange={e =>
                      handleChange(instanceKey, 'salary_amount', e.target.value)
                    }
                    sx={{ width: 150 }}
                  />
                </Box>

                <TextField
                  fullWidth
                  label='How many hours a day did you work?'
                  value={instance.hours_per_day}
                  onChange={e =>
                    handleChange(instanceKey, 'hours_per_day', e.target.value)
                  }
                />

                <Box>
                  <Typography variant='subtitle1' gutterBottom>
                    Were there overtime Opportunities?
                  </Typography>
                  <FormControl component='fieldset' sx={{ mb: 2 }}>
                    <RadioGroup
                      value={instance.overtime_opportunities}
                      onChange={e =>
                        handleChange(
                          instanceKey,
                          'overtime_opportunities',
                          e.target.value
                        )
                      }
                    >
                      <FormControlLabel
                        value='yes'
                        control={<Radio />}
                        label='Yes'
                      />
                      <FormControlLabel
                        value='no'
                        control={<Radio />}
                        label='No'
                      />
                    </RadioGroup>
                  </FormControl>
                  {instance.overtime_opportunities === 'yes' && (
                    <TextField
                      fullWidth
                      label='(If Yes, How many hours a week)'
                      value={instance.overtime_hours_per_week}
                      onChange={e =>
                        handleChange(
                          instanceKey,
                          'overtime_hours_per_week',
                          e.target.value
                        )
                      }
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {/* Reason for Leaving */}
            <Box>
              <Typography variant='h6' gutterBottom>
                Reason for Leaving
              </Typography>
              <TextField
                fullWidth
                label='Reason for Leaving?'
                multiline
                rows={3}
                value={instance.reason_for_leaving}
                onChange={e =>
                  handleChange(
                    instanceKey,
                    'reason_for_leaving',
                    e.target.value
                  )
                }
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Previous Employment History
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {renderEmploymentInstance('previous_employment_1', 1)}
        {renderEmploymentInstance('previous_employment_2', 2)}
        {renderEmploymentInstance('previous_employment_3', 3)}
      </Box>
    </Box>
  );
};

export default PreviousEmploymentHistory;
